import { FC, useMemo, useState } from 'react';
import { LlmSessionInfo } from 'src/types/llmInfo';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import {
    EmptyState,
    MessageBlock,
    MessageBody,
    MessageChevron,
    MessageHeader,
    MessagePreview,
    MessageRoleLabel,
    MessageText,
    NoTranscriptHint,
    RawJson,
    SessionType,
    ThinkingBlock,
    ThinkingBody,
    ThinkingHeader,
    TranscriptCost,
    TranscriptHeader,
    TranscriptHeaderLeft,
    TranscriptPanel,
    TranscriptSessionId,
    TranscriptSessionLabel,
    TranscriptStream
} from '../styled';
import ToolCard from './ToolCard';
import { parseTranscript, RenderBlock } from './parseTranscript';

interface Props {
    session: LlmSessionInfo | null;
}

const truncatePreview = (text: string, max = 80) => {
    const firstLine = text.split('\n')[0];
    return firstLine.length > max
        ? firstLine.slice(0, max - 1) + '…'
        : firstLine;
};

const Thinking: FC<{ text: string }> = ({ text }) => {
    const [open, setOpen] = useState(false);
    return (
        <ThinkingBlock>
            <ThinkingHeader
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                {open ? '▼' : '▶'} Thinking
            </ThinkingHeader>
            {open && <ThinkingBody>{text}</ThinkingBody>}
        </ThinkingBlock>
    );
};

const CollapsibleMessage: FC<{
    role: 'user' | 'assistant' | 'system';
    text: string;
}> = ({ role, text }) => {
    const [open, setOpen] = useState(role === 'assistant');
    return (
        <MessageBlock role={role}>
            <MessageHeader
                role={role}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <MessageRoleLabel>{role}</MessageRoleLabel>
                {!open && (
                    <MessagePreview>{truncatePreview(text)}</MessagePreview>
                )}
                <MessageChevron open={open}>▶</MessageChevron>
            </MessageHeader>
            {open && (
                <MessageBody>
                    {role === 'assistant' ? (
                        <MarkdownEditor value={text} readOnly inline />
                    ) : (
                        <MessageText>{text}</MessageText>
                    )}
                </MessageBody>
            )}
        </MessageBlock>
    );
};

const renderBlock = (block: RenderBlock, idx: number) => {
    switch (block.kind) {
        case 'text':
            return (
                <CollapsibleMessage
                    key={idx}
                    role={block.role}
                    text={block.text}
                />
            );
        case 'thinking':
            return <Thinking key={idx} text={block.text} />;
        case 'tool':
            return (
                <ToolCard
                    key={idx}
                    name={block.name}
                    input={block.input}
                    result={block.result}
                    isError={block.isError}
                />
            );
        default:
            return null;
    }
};

const formatCost = (value: number | null | undefined): string => {
    if (typeof value !== 'number') return '—';
    return `$${value.toFixed(4)}`;
};

const SessionTranscript: FC<Props> = ({ session }) => {
    const blocks = useMemo(
        () => (session ? parseTranscript(session.details) : []),
        [session]
    );

    if (!session) {
        return (
            <TranscriptPanel>
                <EmptyState>
                    Select a session to view its transcript.
                </EmptyState>
            </TranscriptPanel>
        );
    }

    return (
        <TranscriptPanel>
            <TranscriptHeader>
                <TranscriptHeaderLeft>
                    {session.llmSessionType && (
                        <SessionType>{session.llmSessionType}</SessionType>
                    )}
                    <TranscriptSessionLabel>
                        Session{' '}
                        <TranscriptSessionId>
                            {session.sessionId ?? '—'}
                        </TranscriptSessionId>
                    </TranscriptSessionLabel>
                </TranscriptHeaderLeft>
                <TranscriptCost>
                    Cost: {formatCost(session.costUsd)}
                </TranscriptCost>
            </TranscriptHeader>

            {blocks.length === 0 ? (
                <>
                    <NoTranscriptHint>
                        No recognizable transcript — raw response:
                    </NoTranscriptHint>
                    <RawJson>
                        {session.details
                            ? JSON.stringify(session.details, null, 2)
                            : '(empty)'}
                    </RawJson>
                </>
            ) : (
                <TranscriptStream>
                    {blocks.map((b, i) => renderBlock(b, i))}
                </TranscriptStream>
            )}
        </TranscriptPanel>
    );
};

export default SessionTranscript;
